import { createAjv } from "@jsonforms/core";
import DownloadIcon from "@mui/icons-material/Download";
import Drawer from "@mui/material/Drawer";
import { Button, Icon, Notification, Toggle } from "pepsico-ds";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GlobalConfigContext } from "../../../../context/GlobalConfigContext";
import {
  applyEditableFieldRules,
  updatePropertyConstraintInSchema,
} from "../../../../utils/commons";
import { FeatureToggle, useFeatureToggle } from "../../../../utils/featureToggle";
import JsonFormsWrapper from "../../../jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import {
  partnerCodeGroupJsonSchema as partnerCodeGroupJsonSchemaNew,
  partnerCodeGroupUISchema as partnerCodeGroupUISchemaNew,
} from "../../schemas/parterCodeGroupSchemaNew";
import {
  partnerCodeGroupJsonSchema as partnerCodeGroupJsonSchemaOld,
  partnerCodeGroupUISchema as partnerCodeGroupUISchemaOld,
} from "../../schemas/partnerCodeGroupSchema";
import "./createPartnerCodeModal.scss";
import { usePartnerCodeGroupForm } from "./usePartnerCodeGroupForm";

function PartnerCodeGroupFormModal({
  isOpen,
  partnerCodeGroupData,
  setPartnerCodeGroupData,
  handleMutation,
  loading,
  title,
  toggleCreatePartnerCodeModal,
  isEditMode = false,
  showToggle = false,
  codeGroupType = "UPLOAD_LIST",
}) {
  const ajvInstance = useMemo(() => createAjv(), []);
  const isUnlimitedPartnerCodeEnabled =
    useFeatureToggle(FeatureToggle.enableUnlimitedPartnerCode) ?? false;

  const partnerCodeGroupJsonSchema = isUnlimitedPartnerCodeEnabled
    ? partnerCodeGroupJsonSchemaNew
    : partnerCodeGroupJsonSchemaOld;
  const partnerCodeGroupUISchema = isUnlimitedPartnerCodeEnabled
    ? partnerCodeGroupUISchemaNew
    : partnerCodeGroupUISchemaOld;

  const editableFields = useMemo(
    () => [
      "endDate",
      "rewardItemFileData",
      "name",
      "description",
      "codeWarningLimitPercentage",
      "notificationEmails",
    ],
    []
  );
  const jsonSchema = useMemo(() => {
    if (isEditMode && isUnlimitedPartnerCodeEnabled) {
      // In edit mode, remove the rewardItemFileData requirement since files are already uploaded
      const editSchema = { ...partnerCodeGroupJsonSchema };
      editSchema.allOf = partnerCodeGroupJsonSchema.allOf.map((condition) => {
        if (condition.if?.properties?.codeGroupType?.const === "UPLOAD_LIST") {
          return {
            ...condition,
            then: {
              ...condition.then,
              required: [],
              properties: {},
            },
          };
        }
        return condition;
      });
      return editSchema;
    }
    return partnerCodeGroupJsonSchema;
  }, [isEditMode, isUnlimitedPartnerCodeEnabled, partnerCodeGroupJsonSchema]);

  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleUploadFile, handleDownloadFile } = usePartnerCodeGroupForm();

  const isValidForm = ajvInstance.validate(jsonSchema, partnerCodeGroupData);
  const { programMarketConfig } = useContext(GlobalConfigContext);
  const [prevData] = useState(partnerCodeGroupData);

  const handleIsActive = useCallback(
    (isActive) => {
      setPartnerCodeGroupData((prev) => ({ ...prev, isActive }));
    },
    [setPartnerCodeGroupData]
  );

  const isReadyForActive = useMemo(() => {
    const startDate = new Date(partnerCodeGroupData?.startDate);
    const today = new Date();
    return startDate.getTime() <= today.getTime();
  }, [partnerCodeGroupData?.startDate]);

  const handleSubmitButton = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    let hasError = false;

    const files =
      partnerCodeGroupData?.rewardItemFileData?.filter((file) => file?.id == null) ||
      [];

    if (files.length > 0) {
      const uploadResponse = await handleUploadFile(files);

      if (!uploadResponse.status) {
        setError(uploadResponse.message);
        hasError = true;
      } else {
        uploadResponse.data.forEach((file) => {
          const fileIndex = partnerCodeGroupData.rewardItemFileData?.findIndex(
            (f) => f.name === file.name
          );
          partnerCodeGroupData.rewardItemFileData =
            partnerCodeGroupData.rewardItemFileData.filter(
              (f) => f.name !== file.name
            );
          if (fileIndex === -1) partnerCodeGroupData.rewardItemFileData.push(file);
          else partnerCodeGroupData.rewardItemFileData[fileIndex] = file;
        });
      }
    }

    if (!hasError) {
      await handleMutation(partnerCodeGroupData);
    }

    setIsSubmitting(false);
  }, [partnerCodeGroupData, handleMutation, handleUploadFile, isSubmitting]);

  useEffect(() => {
    setError(null);
  }, [partnerCodeGroupData]);

  const isAssociatedPartnerCodeGroupEditable = useMemo(
    () => programMarketConfig?.isAssociatedPartnerCodeGroupEditable || false,
    [programMarketConfig?.isAssociatedPartnerCodeGroupEditable]
  );

  const canEdit = isEditMode && isAssociatedPartnerCodeGroupEditable;

  const getUISchema = useCallback(() => {
    const updatedSchema = updatePropertyConstraintInSchema(
      partnerCodeGroupUISchema,
      "#/properties/endDate",
      "minimum",
      prevData?.endDate
    );

    return canEdit
      ? applyEditableFieldRules(updatedSchema, editableFields)
      : applyEditableFieldRules(partnerCodeGroupUISchema, []); // Pass empty array to ensure no disabled/enabled props
  }, [editableFields, canEdit, prevData?.endDate, partnerCodeGroupUISchema]);

  const handleFormChange = useCallback(
    (data) => {
      if (
        canEdit &&
        new Date(partnerCodeGroupData?.endDate) > new Date(data.endDate)
      ) {
        setError("End date cannot be less than the previous active end date.");
      } else {
        setPartnerCodeGroupData(data);
      }
    },
    [setPartnerCodeGroupData, canEdit, partnerCodeGroupData?.endDate]
  );

  const handleClose = () => {
    if (isSubmitting) return;
    toggleCreatePartnerCodeModal();
  };

  return (
    <Drawer
      className="partner-code-group-modal"
      open={isOpen}
      onClose={handleClose}
      anchor="right"
      classes={{ paper: "partner-code-group-drawer" }}
    >
      <div className="partner-code-group-drawer-header">
        <h2 className="partner-code-group-drawer-title">
          {title} Digital Reward Group
        </h2>
        <button
          type="button"
          aria-label="Close"
          className="partner-code-group-close-button"
          onClick={handleClose}
        >
          <Icon icon="close" />
        </button>
      </div>
      <div className="partner-code-group-drawer-content">
        <div className={`code-group-mod ${isEditMode ? "edit-form" : ""}`}>
          <JsonFormsWrapper
            jsonschema={jsonSchema}
            uischema={getUISchema()}
            data={partnerCodeGroupData}
            onChange={handleFormChange}
          />
          {codeGroupType === "UPLOAD_LIST" && (
            <>
              <Button
                size="small"
                variant="secondary"
                onClick={() =>
                  handleDownloadFile(
                    "text/plain",
                    "sampleFile.txt",
                    "PARTNER_CODE_GROUP"
                  )
                }
                className="download-sample-file mt-4"
              >
                Download sample file <DownloadIcon fontSize="small" />
              </Button>
              <Notification
                className="partner-code-limit-info-note"
                type="info"
                title={
                  <div className="partner-code-limit-info-content">
                    <p className="partner-code-limit-info-title">
                      Code/Link Usage Format Guide
                    </p>
                    <p>
                      Upload codes/links using the format:&nbsp;
                      <code>&lt;Code/Link&gt;,&lt;Usage Limit&gt;</code>
                    </p>
                    <p>For Usage Limit:</p>
                    <ul>
                      <li>
                        <strong>-1</strong> = Unlimited Usage
                      </li>
                      <li>
                        <strong>1 to N (e.g., 5, 100)</strong> = Limited Code Usage
                      </li>
                    </ul>
                    <p className="partner-code-limit-info-title">Examples</p>
                    <ul>
                      <li>
                        <code>CODE12345,-1</code> &rarr; Unlimited usage
                      </li>
                      <li>
                        <code>CODE12221,5</code> &rarr; Can be used 5 times
                      </li>
                    </ul>
                  </div>
                }
                text=""
              />
            </>
          )}
          {error && <p className="errorMessage">{error}</p>}
        </div>
      </div>
      <div className="partner-code-group-drawer-footer">
        {showToggle && (
          <Toggle
            label="Active"
            onUpdate={handleIsActive}
            size="medium"
            checked={partnerCodeGroupData?.isActive}
            disabled={!isReadyForActive}
          />
        )}

        <Button
          onClick={handleSubmitButton}
          disabled={isSubmitting || !isValidForm || loading || error}
        >
          {isSubmitting ? "Submitting..." : title === "Edit" ? "Update" : "Create"}
        </Button>
      </div>
    </Drawer>
  );
}

export default PartnerCodeGroupFormModal;
